import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlSegmentGroup } from '@angular/router';
import { inject, Injectable } from '@angular/core';

import { getProperty, getPropertyValue } from '@core/utilities/generics-utilities';

@Injectable({ providedIn: 'root' })
export class RouterUtilService {
  #router = inject(Router);

  closeCurrentOutlet(): void {
    const lastOutlet = [...this.getActiveOutlets().keys()].at(-1);
    if (lastOutlet) {
      // Close the last outlet.
      this.closeOutlet(lastOutlet);
    }
  }

  closeOutlet(outletName: string): Promise<boolean> {
    const currentUrlTree = this.#router.parseUrl(this.#router.routerState.snapshot.url);
    const newRootSegment = this.#recursivelyClearOutlet(currentUrlTree.root, outletName);
    currentUrlTree.root = newRootSegment;

    return this.#router.navigateByUrl(currentUrlTree);
  }

  /**
   * @desc Retrieve all active outlet. Note: this will not work with nested named outlet.
   */
  getActiveOutlets(): Map<string, ActivatedRouteSnapshot> {
    const map: Map<string, ActivatedRouteSnapshot> = new Map<string, ActivatedRouteSnapshot>();

    // Process each child on the root snapshot.
    this.#router.routerState.snapshot.root?.children?.forEach(child => {
      this.#internalProcessActiveOutlets(map, child);
    });

    return map;
  }

  /**
   * @desc Retrieve all data params and url params from object T.
   * @param {T} object Current route.
   * @param {Router | RouterStateSnapshot} entryPoint Either the router or router state snapshot instance.
   */
  getApplicationRouteDataFromRouterStateSnapshot<T>(
    object: T,
    entryPoint: Router | RouterStateSnapshot,
  ): T | undefined {
    if (!object) {
      return undefined;
    }

    const rootSnapshot = entryPoint instanceof Router ? entryPoint.routerState.snapshot.root : entryPoint.root;

    // Retrieve all of the keys along with their types.
    const map: Map<string, string> = new Map<string, string>(
      Object.keys(object).map(key => {
        const keyType = typeof getProperty(object, key as keyof T);
        // Setting the key to undefined will allow to validate duplicates.
        object[key] = undefined;
        return [key, keyType];
      }),
    );

    // Process each child on the root snapshot.
    rootSnapshot?.children?.forEach(child => {
      this.#internalProcessApplicationRouteDataFromRouterStateSnapshot(object, map, child);
    });

    // Return the result.
    return object;
  }

  /**
   * @desc T and dataKeys must be the same.
   * @param {ActivatedRoute} activatedRoute Current route.
   * @param {string | string[]} dataKeys Data keys to retrieve.
   */
  getApplicationRouteDataFromSnapshot3<T>(object: T, activatedRoute: ActivatedRoute): T | undefined {
    if (!object) {
      return undefined;
    }

    // Retrieve all of the keys along with their types.
    const map: Map<string, string> = new Map<string, string>(
      Object.keys(object).map(key => [key, typeof getProperty(object, key as keyof T)]),
    );
    // Process the current route.
    const result = this.#internalGetApplicationRouteDataFromSnapshot3(object, activatedRoute, map);
    // Set the keys that were not found to undefined.
    map.forEach((type: string, key: string) => {
      object[key] = undefined;
    });

    // Return the result.
    return result;
  }

  #internalGetApplicationRouteDataFromSnapshot3<T>(
    object: T,
    activatedRoute: ActivatedRoute,
    map: Map<string, string>,
  ): T {
    // The route was not found, or there are no keys.
    if (!activatedRoute || !map.size) {
      return object;
    }

    // Retrieve the snapshot.
    const snapshot = activatedRoute.snapshot;

    // Loop through each key that represents an interest, and if they do exist add them to the map and remove them from the keys array;
    map.forEach((type: string, key: string) => {
      // Validate if a route parameter exists.
      const value = snapshot.paramMap.get(key);
      if (value) {
        object[key] = getPropertyValue(type, value);
        map.delete(key);
      }
      // Otherwise, look at the snapshot data.
      else {
        const data = snapshot.data[key];
        if (data) {
          object[key] = getPropertyValue(type, data);
          map.delete(key);
        }
      }
    });

    // At least one key remains to be found, validate the immediate parent.
    if (map.size && activatedRoute.parent) {
      return this.#internalGetApplicationRouteDataFromSnapshot3(object, activatedRoute.parent, map);
    }

    return object;
  }

  #recursivelyClearOutlet(root: UrlSegmentGroup, outletName: string): UrlSegmentGroup {
    const newChildren = {};
    for (const [childOutlet, child] of Object.entries(root.children)) {
      if (childOutlet !== outletName) {
        const newChild = this.#recursivelyClearOutlet(child, outletName);
        newChildren[childOutlet] = newChild;
      }
    }
    return new UrlSegmentGroup(root.segments, newChildren);
  }

  #internalProcessActiveOutlets(
    map: Map<string, ActivatedRouteSnapshot>,
    routeSnapshot: ActivatedRouteSnapshot,
  ): Map<string, ActivatedRouteSnapshot> | null {
    // The route was not found, or there are no keys.
    if (!routeSnapshot) {
      return map;
    }

    map.set(routeSnapshot.outlet, routeSnapshot);

    // Process the children of the current route snapshot.
    routeSnapshot.children.forEach(child => {
      this.#internalProcessActiveOutlets(map, child);
    });

    return null;
  }

  #internalProcessApplicationRouteDataFromRouterStateSnapshot<T>(
    object: T,
    map: Map<string, string>,
    routeSnapshot: ActivatedRouteSnapshot,
  ): T | null {
    // The route was not found, or there are no keys.
    if (!routeSnapshot || !map.size) {
      return object;
    }

    // Loop through each key that represents an interest, and if they do exist add them to the map;
    map.forEach((type: string, key: string) => {
      // Validate if a key already exists.
      const existingKey = !!object[key];
      let hasValue = false;
      // Validate if a route parameter exists.
      const value = routeSnapshot.paramMap.get(key);
      if (value) {
        hasValue = true;
        object[key] = getPropertyValue(type, value);
      }
      // Otherwise, look at the snapshot data.
      else {
        const data = routeSnapshot.data[key];
        if (data) {
          hasValue = true;
          const value = getPropertyValue(type, data);
          object[key] = value;
          // If there is another key that ends with FromURL, try to retrieve the URL as well.
          const keyFromURL = `${key}FromURL`;
          if (map.has(keyFromURL)) {
            const typeFromURL = map.get(keyFromURL);
            const valueFromURL = routeSnapshot.paramMap.get(value + '');
            object[keyFromURL] = getPropertyValue(typeFromURL, valueFromURL);
          }
        }
      }

      // A duplicate key was found which is not allowed.
      if (hasValue && existingKey) {
        throw new Error('Duplicate keys are not allowed');
      }
    });

    // Process the children of the current route snapshot.
    routeSnapshot.children.forEach(child => {
      this.#internalProcessApplicationRouteDataFromRouterStateSnapshot(object, map, child);
    });

    return null;
  }
}
